import React, { type ReactNode } from 'react';
import { t } from './primitives';

/** Real asset URLs pulled from the LEO Pharma campaign work already delivered. */
export const LEO_ASSETS = {
  anzupgoStart: 'https://cdn.magicpatterns.com/uploads/881jkJEAqmuZwxE7RaybGa/Screenshot_2026-06-19_at_09.29.53.png',
  anzupgoHands: 'https://cdn.magicpatterns.com/uploads/i8SWQ7fArduUdFHejCXgu1/Screenshot_2026-06-19_at_08.57.24.png',
  anzupgoVideoPoster: 'https://cdn.magicpatterns.com/uploads/gX8cCuRy5nsdX3YVpcSc7T/Screenshot_2026-06-19_at_08.56.57.png',
  campaignFlow: 'https://cdn.magicpatterns.com/uploads/wWheb1ahK2YTaF9M2yD9Va/Screenshot_2026-06-29_at_11.23.35.png',
  anzupgoTubes: 'https://cdn.magicpatterns.com/uploads/2evvkdLNjqRnzgEzp1i1EW/Screenshot_2026-06-29_at_09.44.40.png',
  dermaWorld: 'https://cdn.magicpatterns.com/uploads/mK7j8iC2uQVdyptCYZZirH/Screenshot_2026-06-29_at_10.12.37.png'
};

/** Real slides from the Leo Pharma UK — DERMX webinar series proposal. */
export const DERMX_SCREENS = {
  summary: 'https://cdn.magicpatterns.com/previews/design-screens/c54623e2-9f7e-48d5-9bf0-8025deb3de4f/0-46385655-3dc4-4928-9dc5-54226ca2b0e3.png',
  offerings: 'https://cdn.magicpatterns.com/previews/design-screens/c54623e2-9f7e-48d5-9bf0-8025deb3de4f/2-6f83a016-1be9-4649-97b5-6ffd3892f86f.png',
  ladder: 'https://cdn.magicpatterns.com/previews/design-screens/c54623e2-9f7e-48d5-9bf0-8025deb3de4f/4-15613726-579e-4c94-aa09-142d4a3bda50.png',
  kpis: 'https://cdn.magicpatterns.com/previews/design-screens/c54623e2-9f7e-48d5-9bf0-8025deb3de4f/6-9ed477bb-ffc0-41a8-9ee5-6ad41fb06676.png'
};
export const LEO_GREEN = '#00543D';
export const LEO_GREEN_LIGHT = '#4A9B7F';
type FrameProps = {
  label: string;
  children: ReactNode;
  className?: string;
};

/** Minimal browser chrome used for platform and web-campaign mockups. */
export function BrowserFrame({
  label,
  children,
  className
}: FrameProps) {
  return <div className={`flex min-h-0 flex-col overflow-hidden rounded-[0.6vw] border border-white/15 bg-[#101010] ${className ?? ''}`} data-id="element-348" data-mp="dveupc07tybh">
      <div className="flex shrink-0 items-center gap-[0.6vw] border-b border-white/10 bg-white/[0.06] px-[0.9vw] py-[1.1vh]" data-id="element-349" data-mp="3bp9haml18611">
        <span aria-hidden className="flex gap-[0.3vw]" data-id="element-350" data-mp="3gpnoy3iq1dyj">
          {['bg-white/25', 'bg-white/20', 'bg-white/15'].map(tone => <span key={tone} className={`block h-[clamp(3px,0.42vw,8px)] w-[clamp(3px,0.42vw,8px)] rounded-full ${tone}`} data-id="element-351" data-mp="3kpel6mr0omag" />)}
        </span>
        <span className={`${t.label} truncate rounded-full bg-white/[0.08] px-[0.7vw] py-[0.4vh] text-white/45`} data-id="element-352" data-mp="2szjjdqlz344w">
          {label}
        </span>
      </div>
      <div className="min-h-0 flex-1 overflow-hidden" data-id="element-353" data-mp="31sdemxhtee4e">{children}</div>
    </div>;
}

/** Phone chrome used for the ANZUPGO® patient guide mockup. */
export function PhoneFrame({
  children,
  className
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={`relative overflow-hidden rounded-[1.1vw] border border-white/20 bg-black shadow-[0_0_0_1px_rgba(255,255,255,0.05)] ${className ?? ''}`} data-id="element-354" data-mp="21r9n0eq5h5r4">
      <span aria-hidden className="absolute left-1/2 top-[0.7vh] z-10 h-[0.5vh] w-[16%] -translate-x-1/2 rounded-full bg-white/30" data-id="element-355" data-mp="1wqc6s7opals9" />
      {children}
    </div>;
}
type FlowStep = {
  title: string;
  description?: string;
};

/** Horizontal process flow with chevron connectors. */
export function FlowRail({
  steps
}: {
  steps: FlowStep[];
}) {
  return <ol className="flex items-stretch gap-[0.6vw]" data-id="element-356" data-mp="1e0e1u0g798su">
      {steps.map((step, index) => <React.Fragment key={step.title} data-id="element-357" data-mp="193nnpinygna2" data-mpi={index}>
          <li className="flex min-w-0 flex-1 flex-col justify-center rounded-[0.5vw] border border-white/10 bg-white/[0.04] px-[1vw] py-[1.4vh]" data-id="element-358" data-mp="190hze5n5hpfe">
            <span className={`${t.label} font-semibold text-[#A26BFF]`} data-id="element-359" data-mp="1014ln0rzueov">
              {`0${index + 1}`}
            </span>
            <p className={`${t.small} mt-[0.8vh] font-semibold leading-[1.2]`} data-id="element-360" data-mp="v3uyxsvzoh4q">
              {step.title}
            </p>
            {step.description ? <p className={`${t.label} mt-[0.6vh] leading-[1.3] text-white/50`} data-id="element-361" data-mp="304ozeo8djoyx">
                {step.description}
              </p> : null}
          </li>
          {index < steps.length - 1 ? <li aria-hidden className="flex shrink-0 items-center text-[clamp(9px,1vw,20px)] text-white/25" data-id="element-362" data-mp="2qdkktpmm264m">
              ›
            </li> : null}
        </React.Fragment>)}
    </ol>;
}

/** Vertical numbered pipeline with a connecting spine. */
export function Pipeline({
  steps
}: {
  steps: {
    title: string;
    description: string;
  }[];
}) {
  return <ol className="relative flex min-h-0 flex-1 flex-col justify-between" data-id="element-363" data-mp="27gjgm70vsa1q">
      <span aria-hidden className="absolute bottom-[1.4vh] left-[clamp(7px,0.85vw,17px)] top-[1.4vh] w-px bg-white/15" data-id="element-364" data-mp="p0rgmuz3hxm" />
      {steps.map((step, index) => <li key={step.title} className="relative flex items-start gap-[1vw]" data-id="element-365" data-mp="1q65znz513srv" data-mpi={index}>
          <span className={`${t.label} relative z-10 flex h-[clamp(14px,1.7vw,34px)] w-[clamp(14px,1.7vw,34px)] shrink-0 items-center justify-center rounded-full border border-[#7D38E5] bg-[#0C0C0C] font-semibold text-[#A26BFF]`} data-id="element-366" data-mp="1q42zima5wj9f">
            {index + 1}
          </span>
          <div className="min-w-0" data-id="element-367" data-mp="1v3o174gpv5pu">
            <p className={`${t.body} font-semibold leading-[1.2] text-white`} data-id="element-368" data-mp="28w48wikkbrx3">
              {step.title}
            </p>
            <p className={`${t.label} mt-[0.5vh] leading-[1.35] text-white/55`} data-id="element-369" data-mp="23x13gx0o7jol">
              {step.description}
            </p>
          </div>
        </li>)}
    </ol>;
}
type QuadrantPoint = {
  label: string;
  x: number;
  y: number;
  highlight?: boolean;
};

/** Cost-vs-quality plot used to frame the Roche learning. */
export function QuadrantPlot({
  points
}: {
  points: QuadrantPoint[];
}) {
  return <div className="flex min-h-0 flex-1 gap-[0.8vw]" data-id="element-370" data-mp="jjk171hp3d0e">
      <span className={`${t.label} flex items-center justify-center uppercase tracking-[0.2em] text-white/40`} style={{
      writingMode: 'vertical-rl',
      transform: 'rotate(180deg)'
    }} data-id="element-371" data-mp="196mmbh3ay8ti">
        Quality of output
      </span>
      <div className="flex min-h-0 flex-1 flex-col" data-id="element-372" data-mp="1i5859dcorput">
        <div className="relative min-h-0 flex-1 border-b border-l border-white/20" data-id="element-373" data-mp="1n6fbh7z6kgzd">
          <span aria-hidden className="absolute inset-y-0 left-1/2 w-px bg-white/[0.07]" data-id="element-374" data-mp="1s4z1bjgwacx0" />
          <span aria-hidden className="absolute inset-x-0 top-1/2 h-px bg-white/[0.07]" data-id="element-375" data-mp="1w6kivi4djfdj" />
          {points.map(point => <div key={point.label} className="absolute -translate-x-1/2 translate-y-1/2 text-center" style={{
          left: `${point.x}%`,
          bottom: `${point.y}%`
        }} data-id="element-376" data-mp="20wsldfvmu5rc">
              <span aria-hidden className={`mx-auto block rounded-full ${point.highlight ? 'h-[clamp(9px,1.15vw,23px)] w-[clamp(9px,1.15vw,23px)] bg-[#7D38E5] ring-[3px] ring-[#7D38E5]/30' : 'h-[clamp(6px,0.7vw,14px)] w-[clamp(6px,0.7vw,14px)] bg-white/40'}`} data-id="element-377" data-mp="2awjepe8giepu" />
              <span className={`${t.label} mt-[0.8vh] block whitespace-nowrap font-semibold ${point.highlight ? 'text-white' : 'text-white/55'}`} data-id="element-378" data-mp="3qdwg2vcutcqk">
                {point.label}
              </span>
            </div>)}
        </div>
        <span className={`${t.label} mt-[1.2vh] self-end uppercase tracking-[0.2em] text-white/40`} data-id="element-379" data-mp="hycri9n4oihu">
          Cost efficiency →
        </span>
      </div>
    </div>;
}
type Bar = {
  label: string;
  value: number | null;
  n?: number;
};

/** Compact column chart — mirrors the Tralokinumab RWE study visualisation. */
export function MiniBarChart({
  title,
  bars,
  accent = LEO_GREEN_LIGHT
}: {
  title: string;
  bars: Bar[];
  accent?: string;
}) {
  return <div className="flex h-full min-h-0 flex-col" data-id="element-380" data-mp="9a5qrl13z6jc">
      <p className={`${t.label} font-semibold uppercase tracking-[0.16em] text-white/60`} data-id="element-381" data-mp="98cgzwv78674">
        {title}
      </p>
      <div className="mt-[1.2vh] flex min-h-0 flex-1 items-end gap-[0.5vw]" data-id="element-382" data-mp="e2qkkmk0ljmv">
        {bars.map(bar => <div key={bar.label} className="flex h-full min-w-0 flex-1 flex-col justify-end" data-id="element-383" data-mp="7eejj0es6qn">
            {bar.value === null ? <span className={`${t.label} mb-[0.4vh] block text-center text-white/25`} data-id="element-384" data-mp="sxd4rhqca9ww">
                —
              </span> : <>
                <span className={`${t.label} mb-[0.5vh] block text-center font-semibold text-white`} data-id="element-385" data-mp="wx5ie8ulb694">
                  {`${bar.value}%`}
                </span>
                <span aria-hidden className="block w-full rounded-t-[2px]" style={{
            height: `${bar.value}%`,
            backgroundColor: accent
          }} data-id="element-386" data-mp="o0mtd7phc2ht" />
              </>}
            <span className={`${t.label} mt-[0.6vh] block truncate text-center text-white/40`} data-id="element-387" data-mp="317mcux47htu7">
              {bar.label}
            </span>
          </div>)}
      </div>
    </div>;
}

/** Tiny inline sparkline for dashboard rows. */
export function Sparkline({
  points,
  className
}: {
  points: number[];
  className?: string;
}) {
  const max = Math.max(...points, 1);
  const path = points.map((value, index) => {
    const x = index / (points.length - 1) * 100;
    const y = 100 - value / max * 100;
    return `${index === 0 ? 'M' : 'L'}${x},${y}`;
  }).join(' ');
  return <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden className={className} data-id="element-388" data-mp="2mgvmzum6d4tq">
      <path d={path} fill="none" stroke="#A26BFF" strokeWidth={6} vectorEffect="non-scaling-stroke" data-id="element-389" data-mp="1ypznscfe37cg" />
    </svg>;
}
export function StatusToggle({
  on
}: {
  on: boolean;
}) {
  return <span aria-hidden className={`inline-flex h-[clamp(7px,0.9vw,18px)] w-[clamp(14px,1.7vw,34px)] shrink-0 items-center rounded-full px-[0.15vw] ${on ? 'justify-end bg-[#7D38E5]' : 'justify-start bg-white/15'}`} data-id="element-390" data-mp="3gm2f6o1yug4e">
      <span className="block h-[clamp(5px,0.62vw,12px)] w-[clamp(5px,0.62vw,12px)] rounded-full bg-white" data-id="element-391" data-mp="36ljq9evjo9fm" />
    </span>;
}